import axiosInstance, { getApiErrorMessage } from "../auth/apiConfig";

const WEB_BASE = "/api/v1/web/case-papers";

export const mapCasePaperToForm = (detail) => {
  const h = detail?.header;
  if (!h) {
    return {
      casePaperId: null,
      prescriptions: [],
      investServices: [],
    };
  }

  return {
    casePaperId: h.casePaperId,
    bp: h.bp || "",
    pulse: h.pulse || "",
    spo2: h.spo2 || "",
    weight: h.weight || "",
    height: h.height || "",
    rbs: h.bsl || "",
    temp: h.temperature || "",
    resp: h.respiration || "",
    bmi: h.bmi || "",
    chiefComplaints: h.chiefComplaints || "",
    history: h.history || "",
    allergy: h.allergies || "",
    examinations: h.examination || "",
    findings: h.findings || "",
    diagnosis: h.otherDetails || "",
    myThought: h.myThoughts || "",
    followUp: Boolean(h.followUpDateApply),
    followUpDate: h.followupDate || "",
    followUpInstructions: h.followupInstructions || "",
    diabetes: Boolean(h.diabetes),
    diabetesNote: h.diabetesText || "",
    htn: Boolean(h.hypertension),
    htnNote: h.hypertensionText || "",
    cadIhd: Boolean(h.cadIhd),
    cadIhdNote: h.cadIhdText || "",
    thyroid: Boolean(h.thyroid),
    thyroidNote: h.thyroidText || "",
    asthma: Boolean(h.asthma),
    tb: Boolean(h.tb),
    prescriptions: (detail.prescriptions || []).map((p) => ({
      drugId: p.drugId,
      genericId: p.genericId,
      drugName: p.drugName || "",
      genericName: p.genericName || "",
      doseId: p.doseId,
      dose: p.doseName || "",
      duration: p.duration != null ? String(p.duration) : "",
      durIn: p.durationIn || "Days",
      total: p.qty != null ? String(p.qty) : "",
      instruction: p.instruction || "",
      remark: p.remark || "",
    })),
    investServices: (detail.services || []).map((s) => ({
      serviceId: s.serviceId,
      serviceName: s.serviceName || "",
      qty: s.quantity != null ? String(s.quantity) : "1",
    })),
  };
};

export const buildSaveCasePaperPayload = (form, meta = {}) => ({
  visitId: meta.visitId,
  casepaperDate: meta.casepaperDate || new Date().toISOString().slice(0, 10),
  weight: form.weight || "",
  height: form.height || "",
  bmi: form.bmi || "",
  bp: form.bp || "",
  bsl: form.rbs || "",
  spo2: form.spo2 || "",
  temperature: form.temp || "",
  pulse: form.pulse || "",
  respiration: form.resp || "",
  history: form.history || "",
  allergies: form.allergy || "",
  chiefComplaints: form.chiefComplaints || "",
  examination: form.examinations || "",
  findings: form.findings || "",
  familyHistory: "",
  menstrualHistory: "",
  isAnc: false,
  comorbidHistory: "",
  prevTreatmentHistory: "",
  prevInvestigations: form.prevInvest || "",
  myThoughts: form.myThought || "",
  followupDate: form.followUp && form.followUpDate ? form.followUpDate : null,
  followupInstructions: form.followUpInstructions || "",
  otherDetails: form.diagnosis || "",
  diabetes: Boolean(form.diabetes),
  hypertension: Boolean(form.htn),
  noComorbidities: false,
  cadIhd: Boolean(form.cadIhd),
  thyroid: Boolean(form.thyroid),
  asthma: Boolean(form.asthma),
  tb: Boolean(form.tb),
  followUpDateApply: Boolean(form.followUp),
  cadIhdText: form.cadIhdNote || "",
  thyroidText: form.thyroidNote || "",
  diabetesText: form.diabetesNote || "",
  hypertensionText: form.htnNote || "",
  doctorId: meta.doctorId || 0,
  addedBy: meta.userId || 0,
  updatedBy: meta.userId || 0,
  prescriptions: (form.prescriptions || []).map((p) => ({
    drugId: p.drugId || 0,
    genericId: p.genericId || 0,
    drugName: p.drugName || p.name || "",
    genericName: p.genericName || "",
    doseId: p.doseId || 0,
    doseName: p.dose || p.doseName || "",
    duration: p.duration ? Number(p.duration) : 0,
    durationIn: p.durIn || p.durationIn || "Days",
    qty: p.total || p.totalQty ? Number(p.total || p.totalQty) : 0,
    instructionId: 0,
    instruction: p.instruction || "",
    remark: p.remark || "",
  })),
  services: (form.investServices || []).map((s) => ({
    serviceId: s.serviceId || 0,
    serviceName: s.serviceName || s.name || "",
    quantity: s.qty ? Number(s.qty) : 1,
  })),
});

export const getCasePaperByVisit = async (visitId) => {
  try {
    const response = await axiosInstance.get(WEB_BASE, { params: { visitId } });
    return response.data?.data ?? null;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to load case paper"));
  }
};

export const getCasePaperById = async (casePaperId) => {
  try {
    const response = await axiosInstance.get(`${WEB_BASE}/${casePaperId}`);
    return response.data?.data ?? null;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to load case paper"));
  }
};

export const createCasePaper = async (payload) => {
  try {
    const response = await axiosInstance.post(WEB_BASE, payload);
    return response.data?.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to save case paper"));
  }
};

export const updateCasePaper = async (casePaperId, payload) => {
  try {
    const response = await axiosInstance.put(`${WEB_BASE}/${casePaperId}`, payload);
    return response.data?.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to update case paper"));
  }
};

const mapLookupOption = (item) => ({
  id: item?.id,
  value: item?.id,
  label: item?.name || `Item ${item?.id}`,
  genericName: item?.extra || "",
  data: item,
});

/** Active services — shared Get_ActiveServices_web via doctor-rounds. */
export const searchCasePaperServices = async (query, top = 50) => {
  try {
    const response = await axiosInstance.get("/api/v1/web/doctor-rounds/services", {
      params: { q: query?.trim() || undefined, top },
    });
    return (response.data?.data ?? []).map((item) => ({
      id: item.serviceId,
      value: item.serviceId,
      label: item.serviceName || `Service ${item.serviceId}`,
      data: {
        serviceId: item.serviceId,
        code: item.serviceCode || "",
        serviceName: item.serviceName || "",
      },
    }));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to search services"));
  }
};

/** Active drugs — shared Get_ActiveDrugs_web via discharge-summaries. */
export const searchCasePaperDrugs = async (query, top = 50) => {
  try {
    const response = await axiosInstance.get("/api/v1/web/discharge-summaries/drugs", {
      params: { q: query?.trim() || undefined, top },
    });
    return (response.data?.data ?? []).map(mapLookupOption);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to search drugs"));
  }
};

/** Active doses — shared Get_ActiveDoses_web via discharge-summaries. */
export const searchCasePaperDoses = async (query, top = 50) => {
  try {
    const response = await axiosInstance.get("/api/v1/web/discharge-summaries/doses", {
      params: { q: query?.trim() || undefined, top },
    });
    return (response.data?.data ?? []).map(mapLookupOption);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to search doses"));
  }
};
